import { ChangeDetectionStrategy, Component, Input } from '@angular/core';

export interface BarPoint {
  label: string;
  /** The bar's height. */
  value: number;
  /** An optional second figure drawn as a darker bar inside the first (e.g. revenue within gross). */
  inner?: number;
}

/**
 * A small column chart in the dashboard palette: one bar per point, an
 * optional darker inner bar, the label under each and the value on hover.
 * A visually hidden table carries the same numbers for screen readers.
 */
@Component({
  selector: 'admin-bar-chart',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <figure class="chart">
      <div class="bars" aria-hidden="true">
        @for (p of points; track p.label) {
          <div
            class="col"
            [title]="
              p.label +
              ': ' +
              format(p.value) +
              (p.inner !== undefined ? ' (' + innerLabel + ' ' + format(p.inner) + ')' : '')
            "
          >
            <div class="bar" [style.height.%]="pct(p.value)">
              @if (p.inner !== undefined) {
                <div class="inner" [style.height.%]="p.value ? (p.inner / p.value) * 100 : 0"></div>
              }
            </div>
            <span class="label">{{ p.label }}</span>
          </div>
        }
      </div>
      @if (innerLabel) {
        <figcaption class="legend">
          <span><i class="swatch outer"></i>{{ valueLabel }}</span>
          <span><i class="swatch inner-swatch"></i>{{ innerLabel }}</span>
        </figcaption>
      }
      <table class="visually-hidden">
        <caption>
          {{
            valueLabel
          }}
          by
          {{
            caption || 'period'
          }}
        </caption>
        <tr>
          <th scope="col">Period</th>
          <th scope="col">{{ valueLabel }}</th>
          @if (innerLabel) {
            <th scope="col">{{ innerLabel }}</th>
          }
        </tr>
        @for (p of points; track p.label) {
          <tr>
            <th scope="row">{{ p.label }}</th>
            <td>{{ format(p.value) }}</td>
            @if (innerLabel) {
              <td>{{ format(p.inner ?? 0) }}</td>
            }
          </tr>
        }
      </table>
    </figure>
  `,
  styles: [
    `
      .chart {
        margin: 0;
      }
      .bars {
        display: flex;
        align-items: flex-end;
        gap: 8px;
        height: 200px;
        padding-top: 8px;
        border-bottom: 1px solid var(--cnt-line, rgba(34, 34, 34, 0.08));
      }
      .col {
        display: flex;
        flex: 1 1 0;
        flex-direction: column;
        justify-content: flex-end;
        align-items: stretch;
        height: 100%;
        min-width: 0;
        position: relative;
      }
      .bar {
        position: relative;
        display: flex;
        align-items: flex-end;
        min-height: 2px;
        border-radius: 6px 6px 0 0;
        background: rgba(227, 83, 13, 0.25);
        transition: height 0.4s ease;
      }
      .inner {
        width: 100%;
        border-radius: 6px 6px 0 0;
        background: var(--cnt-trinidad, #e3530d);
      }
      .label {
        position: absolute;
        bottom: -22px;
        left: 0;
        right: 0;
        color: var(--cnt-muted, #707070);
        font-size: 0.7rem;
        font-weight: 700;
        text-align: center;
      }
      .legend {
        display: flex;
        gap: 16px;
        margin-top: 32px;
        color: var(--cnt-muted, #707070);
        font-size: 0.8rem;
      }
      .swatch {
        display: inline-block;
        width: 10px;
        height: 10px;
        margin-right: 6px;
        border-radius: 3px;
        vertical-align: -1px;
      }
      .outer {
        background: rgba(227, 83, 13, 0.25);
      }
      .inner-swatch {
        background: var(--cnt-trinidad, #e3530d);
      }
      .visually-hidden {
        position: absolute;
        width: 1px;
        height: 1px;
        overflow: hidden;
        clip: rect(0 0 0 0);
        white-space: nowrap;
      }
      @media (prefers-reduced-motion: reduce) {
        .bar {
          transition: none;
        }
      }
    `,
  ],
})
export class AdminBarChartComponent {
  @Input() points: BarPoint[] = [];
  @Input() valueLabel = 'Value';
  @Input() innerLabel = '';
  @Input() caption = '';
  /** How values read in tooltips and the table; whole dollars by default. */
  @Input() format: (n: number) => string = (n) =>
    n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });

  pct(value: number): number {
    const max = Math.max(...this.points.map((p) => p.value), 0);
    return max ? (value / max) * 100 : 0;
  }
}
